import React, { useContext, useLayoutEffect, useRef, useState } from 'react';
import { graphql, Link } from 'gatsby';
import { Global, css } from '@emotion/react';
import Image from 'gatsby-plugin-sanity-image';
import Vimeo from '@u-wave/react-vimeo';
import { Parallax } from 'react-scroll-parallax';
import ParallaxCache from'../components/parallax-cache';
import { ThemeContext } from '../components/theme-provider';
import SEO from '../components/seo';
import FadeIn from '../components/fade-in';
import PortableText from '../components/portable-text';
import Button from '../components/button';
import Accordion from '../components/accordion';
import Gallery from '../components/gallery';
import Video from '../components/video';
import ProjectTable from '../components/project-table';
import ArrowDown from '../images/svg/arrow-down.svg';
import ArrowUpRight from '../images/svg/arrow-up-right.svg';
import ArrowRight from '../images/svg/arrow-right.svg';
import ArrowLeft from '../images/svg/arrow-left.svg';

export default function Project({ data }) {
  const theme = useContext(ThemeContext);
  const [showIntro, setShowIntro] = useState(false);
  const [formStatus, setFormStatus] = useState('idle');
  const formLoadTime = useRef(Date.now());

  useLayoutEffect(() => {
    if (data.project.introForegroundColor === 'dark') {
      theme.setLogoColorDark(true);
      theme.setMenuColorDark(true);
    } else {
      theme.setLogoColorDark(false);
      theme.setMenuColorDark(false);
    }
    theme.setBackgroundColor(null);

    setTimeout(() => {
      setShowIntro(1);
    }, 3000);
  }, [data.project.introForegroundColor]);

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    if (Date.now() - formLoadTime.current < 2000) {
      return;
    }

    setFormStatus('submitting');
    
    const formData = new FormData(e.target);
    const enquiryUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`;
    
    const submitData = {
      firstName: formData.get('firstName'),
      lastName: formData.get('lastName'),
      email: formData.get('email'),
      telephone: formData.get('telephone'),
      postCode: formData.get('postCode'),
      website: formData.get('website'),
      enquiryUrl: enquiryUrl,
      projectName: data.project.projectName,
      projectId: data.project.projectId,
      rotationName: data.project.rotationName,
    };

    try {
      const response = await fetch('/api/eoi-form', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        setFormStatus('success');
        e.target.reset();
      } else {
        setFormStatus('error');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setFormStatus('error');
    }
  };

  const handleEoiCompletionClick = (e) => {
    const link = e.target.closest('a');
    if (link && link.href) {
      e.preventDefault();
      window.open(link.href, '_blank', 'noopener,noreferrer');
    }
  };

  function renderContentBlock(content) {
    switch(content._type) {
      case 'image':
        return (
          <FadeIn
            css={css`
              grid-column: span 12;
              line-height: 0;
            `}
          >
            {content.asset && <Image
              {...content}
              width={1980}
              style={{
                width: '100%',
              }}
            />}
          </FadeIn>
        );
      case 'doubleImage':
        return (
          <FadeIn
            css={css`
              grid-column: span 12;
              line-height: 0;

              @media (max-width: 900px) {
                img {
                  width: 100% !important;
                  height: 100vw !important;
                }
              }
            `}
          >
            {content.imageOne?.asset && <Image
              {...content.imageOne}
              width={990}
              height={990}
              style={{
                width: '50%',
                height: '50vw',
                objectFit: 'cover',
              }}
            />}
            {content.imageTwo?.asset && <Image
              {...content.imageTwo}
              width={990}
              height={990}
              style={{
                width: '50%',
                height: '50vw',
                objectFit: 'cover',
              }}
            />}
          </FadeIn>
        );
      case 'gallery':
        return (
          <FadeIn>
            <Gallery images={content.images} />
          </FadeIn>
        );
      case 'imageText':
        return (
          <FadeIn
            css={css`
              display: grid;
              align-items: end;
              grid-template-columns: repeat(12, 1fr);
              gap: var(--gutter);
              padding: 0 var(--margin);
            `}
          >
            {content.image?.asset && <Image
              {...content.image}
              width={1980}
              css={css`
                grid-row-start: 1;
                grid-column-start: ${(content.imagePosition === 'left') ? 1 : 7};
                grid-column-end: span 6;
                border: 1px solid var(--black);

                @media (max-width: 900px) {
                  grid-row-start: auto;
                  grid-column-start: 1;
                  grid-column-end: span 12;
                  margin-bottom: 2rem;
                }
              `}
              style={{
                width: '100%',
              }}
            />}
            <div
              css={css`
                grid-row-start: 1;
                grid-column-start: ${(content.imagePosition === 'left') ? 9 : 1};
                grid-column-end: span 4;
                margin-bottom: -0.9rem;

                @media (max-width: 900px) {
                  grid-row-start: auto;
                  grid-column-start: 1;
                  grid-column-end: span 12;
                  margin-bottom: 0;
                }
              `}
            >
              <PortableText blocks={content._rawText} />
            </div>
          </FadeIn>
        );
      case 'imageQuote':
        return (
          <FadeIn
            css={css`
              display: grid;
              grid-template-columns: repeat(12, 1fr);
              gap: var(--gutter);
              padding: 0 var(--margin);
            `}
          >
            {content.image?.asset && <Image
              {...content.image}
              width={1980}
              css={css`
                grid-row-start: 1;
                grid-column-start: ${(content.imagePosition === 'left') ? 1 : 7};
                grid-column-end: span 6;
                border: 1px solid var(--black);

                @media (max-width: 900px) {
                  grid-row-start: auto;
                  grid-column-start: 1;
                  grid-column-end: span 12;
                  margin-bottom: 2rem;
                }
              `}
              style={{
                width: '100%',
              }}
            />}
            <div
              css={css`
                grid-row-start: 1;
                grid-column-start: ${(content.imagePosition === 'left') ? 8 : 1};
                grid-column-end: span 5;

                @media (max-width: 900px) {
                  grid-row-start: auto;
                  grid-column-start: 1;
                  grid-column-end: span 12;
                }
              `}
            >
              <div
                className="type--large"
                css={css`
                  quotes: "“" "”";
                  margin-bottom: 7rem;
                  font-feature-settings: 'ss03' on;

                  @media (max-width: 900px) {
                    margin-bottom: 4rem;
                  }

                  p:first-of-type::before {
                    content: open-quote;
                  }

                  p:last-of-type::after {
                    content: close-quote;
                  }
                `}
              >
                <PortableText blocks={content._rawQuote} />
              </div>
              <div
                className="type--medium"
                css={css`
                  white-space: pre-line;
                `}
              >
                {content.quoteAuthor}
              </div>
            </div>
          </FadeIn>
        );
      case 'heading':
        return (
          <FadeIn>
            <a
              id={content.text?.toString().toLowerCase().trim().replace(/&/g, '-and-').replace(/[\s\W-]+/g, '-')}
              css={css`
                position: relative;
                top: -5rem;
              `}
            />
            <div
              css={css`
                display: grid;
                align-items: end;
                grid-template-columns: repeat(12, 1fr);
                gap: var(--gutter);
                padding: 0 var(--margin);
              `}
            >
              <div
                className="type--large"
                css={css`
                  grid-column: span 6;

                  @media (max-width: 900px) {
                    grid-column: span 12;
                  }
                `}
              >
                {content.text}
              </div>
            </div>
          </FadeIn>
        );
      case 'video':
        return (
          <FadeIn>
            <Video
              title={content.title}
              video={content.video}
              description={content.description}
            />
          </FadeIn>
        );
    }
  }

  return (
    <div>
      <ParallaxCache />
      <SEO
        title={data.project.title}
        description={(data.project._rawOverview) ? data.project._rawOverview.map(el => el.children).flat().map((el) => el?.text)[0] : null}
        imageID={(data.project.introImage) ? data.project.introImage.asset._id : null}
      />
      <Global
        styles={css`
          html {
            scroll-behavior: smooth;
          }
        `}
      />
      <div
        css={css`
          position: fixed;
          top: 0;
          right: 0;
          left: 0;
          height: 100vh;
          z-index: -1;
        `}
      >
        {(data.project.introMediaType === 'image' && data.project.introImage?.asset) &&
          <Image
            {...data.project.introImage}
            width={1980}
            style={{
              width: (data.project.introImageFormat === 'half-width') ? '50%' : '100%',
              height: '100vh',
              objectFit: 'cover',
              marginLeft: (data.project.introImageFormat === 'half-width') ? '50%' : '0',
              opacity: (showIntro) ? 1 : 0,
              transition: 'opacity 0.6s var(--curve)',
            }}
            alt="Intro Image"
            onLoad={() => setShowIntro(1)}
          />
        }
        {(data.project.introMediaType === 'video' && data.project.introVideo) &&
          <Vimeo
            css={css`
              position: absolute;
              top: 50%;
              left: 50%;
              width: 177.77777778vh;
              height: 56.25vw;
              min-width: 100%;
              min-height: 100%;
              transform: translate(-50%, -50%);
              opacity: ${(showIntro) ? 1 : 0};
              transition: opacity 0.6s var(--curve);
            `}
            video={data.project.introVideo}
            onPlay={() => setShowIntro(1)}
            background
            responsive
            loop
          />
        }
      </div>
      <div
        css={css`
          display: flex;
          justify-content: center;
          align-items: center;
          position: absolute;
          top: 0;
          bottom: 0;
          left: var(--margin);
        `}
      >
        <ul
          className="type--extra-small"
          css={css`
            margin: 0;
            padding: 0;

            li {
              position: relative;
              list-style: none;
              margin: 1.6rem 0;

              &::before {
                content: '';
                position: absolute;
                top: 0.45rem;
                left: -1.3rem;
                display: inline-block;
                height: 0.6rem;
                width: 0.6rem;
                background-color: ${(data.project.introForegroundColor === 'dark') ? 'var(--black)' : 'var(--white)'};
                border-radius: 50%;
                opacity: 0;
                transition: opacity 0.6s var(--curve);
              }

              &:hover {
                &::before {
                  opacity: 1;
                }
              }

              a {
                color: ${(data.project.introForegroundColor === 'dark') ? 'var(--black)' : 'var(--white)'};
                text-decoration: none;
              }
            }
          `}
        >
          <li>
            <a href="#overview">Overview</a>
          </li>
          {data.project.content.length > 0 && data.project.content
            .filter(content => content._type === 'heading')
            .map((heading, i) => (
              <li
                key={i}
              >
                <a href={`#${heading.text?.toString().toLowerCase().trim().replace(/&/g, '-and-').replace(/[\s\W-]+/g, '-')}`}>{heading.text}</a>
              </li>
            ))
          }
        </ul>
      </div>
      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: var(--gutter);
          position: absolute;
          right: var(--margin);
          bottom: 1.6rem;
          left: var(--margin);
          color: ${(data.project.introForegroundColor === 'dark') ? 'var(--black)' : 'var(--white)'};
        `}
      >
        <h1
          className="type--medium"
          css={css`
            grid-row-start: 1;
            grid-column: span 10;
            line-height: 1;
            color: ${(data.project.introImageFormat === 'half-width') ? 'var(--black)' : 'inherit'};
            text-transform: uppercase;
          `}
        >{data.project.title}</h1>
        {data.project.status?.status && <div
          className="type--small"
          css={css`
            grid-row-start: 1;
            grid-column-start: 11;
            grid-column-end: span 2;
            z-index: 1;

            @media (max-width: 900px) {
              display: none;
            }
          `}
        >{data.project.status.status}</div>}
        <div
          css={css`
            grid-row-start: 1;
            grid-column-start: 12;
            grid-column-end: span 1;
            text-align: right;
            z-index: 1;
          `}
        >
          <a 
            css={css`
              position: relative;
              right: -1rem;
              bottom: calc(1rem - 0.5rem);
              padding: 1rem;

              svg path {
                fill: ${(data.project.introForegroundColor === 'dark') ? 'var(--black)' : 'var(--white)'};
              }
            `}
            href="#overview"
          >
            <ArrowDown/>
          </a>
        </div>
      </div>
      <div
        css={css`
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: var(--gutter);
          background-color: var(--white);
          padding: 0 var(--margin);
          padding-top: 10rem;
          margin-top: 100vh;

          @media (max-width: 900px) {
            padding-top: 4rem;
          }
        `}
      >
        <div
          css={css`
            grid-column: span 6;

            @media (max-width: 900px) {
              grid-column: span 12;
            }
          `}
        >
          <a
            css={css`
              position: relative;
              top: -10rem;
            `}
            id="overview"
          />  
          <div
            className="type--small-uppercase"
            css={css`
              margin-bottom: 2.5rem;
            `}
          >
            {data.project.projectCode}
          </div>
          <div className="type--large">
            {data.project._rawOverview &&
              <PortableText blocks={data.project._rawOverview} />
            }
          </div>
        </div>
        <div
          className="type--small"
          css={css`
            grid-column-start: 9;
            grid-column-end: span 4;
            margin-top: 4rem;

            @media (max-width: 900px) {
              grid-column-start: 1;
              grid-column-end: span 12;
            }

            > div {
              display: flex;
              justify-content: space-between;
              padding: 2rem 2rem 1.5rem 0;
              border-bottom: 1px solid var(--black);

              &:first-of-type {
                border-top: 1px solid var(--black);
              }
            }
          `}
        >
          {data.project.simpleAddress && <div>
            <div
              className="type--mono"
              css={css`
                white-space: pre-line;
              `}
            >
              {data.project.simpleAddress}
            </div>
            <div>
              <img
                src="/images/pin.svg"
                alt="Pin"
              />
            </div>
          </div>}
          {data.project.completion && <div>
            <div>
              {data.project.completion}
            </div>
            <div>
              <img
                src="/images/circle.svg"
                alt="Circle"
              />
            </div>
          </div>}
          {data.project.information.length > 0 && data.project.information
            .map((information, i) => (
              <Accordion key={i} title={information.title}>
                {information._rawText && <PortableText blocks={information._rawText} />}
              </Accordion>
            ))
          }
          <div>
            <div>
              {data.project.type.length > 0 && data.project.type
                .map((type, i) => <span key={i}>{type?.title}</span>)
                .reduce((prev, curr) => [prev, ' / ', curr])
              }
            </div>
          </div>
          {data.project.scale && <div>
            <div>
              Scale: {data.project.scale}
            </div>
            <div>
              <img
                src="/images/scale.svg"
                alt="Scale"
              />
            </div>
          </div>}
          {data.project.enableEoiForm && <div
            css={css`
              display: block !important;
              padding-bottom: 0 !important;
              padding-right: 0 !important;
              border-bottom: 0 !important;
              margin-top: 2rem;
            `}
          >
            <Button
              css={css`
                width: 100%;
                margin-bottom: 2rem;
                background-color: ${data.project.backgroundColor?.value || 'transparent'};

                > div {
                  margin: 0 auto;

                  &:nth-of-type(2) {
                    display: none;
                  }
                }
              `}
              external={false}
              url="#eoi-form"
              text="Enquire Now"
            />
          </div>}
          {data.project.buttons.length > 0 && <div
            css={css`
              display: block !important;
              padding-bottom: 0 !important;
              padding-right: 0 !important;
              border-bottom: 0 !important;
              margin-top: ${!data.project.enableEoiForm ? '2rem' : '0'};
            `}
          >
            {data.project.buttons
              .map((button, i) => (
                <Button
                  key={i}
                  css={css`
                    width: 100%;
                    margin-bottom: 2rem;
                  `}
                  external={true}
                  url={button.url}
                  text={button.title}
                  arrow={<ArrowUpRight />}
                />
              ))
            }
          </div>}
        </div>
      </div>
      <div
        css={css`
          background-color: var(--white);
          padding-top: 10rem;

          @media (max-width: 900px) {
            padding-top: 4rem;
          }
        `}
      >
        {data.project.content.length > 0 && data.project.content
          .map((content, i) => (
            <div
              key={i}
              css={css`
                padding-bottom: ${content._type === 'heading' ? '4rem' : '13rem'};
              `}
            >
              {renderContentBlock(content, i)}
            </div>
          ))
        }
      </div>
      {data.project.additionalInformation.length > 0 && <div
        css={css`
          display: grid;
          align-items: end;
          grid-template-columns: repeat(12, 1fr);
          gap: var(--gutter);
          background-color: var(--white);
          padding: 8rem var(--margin);
          border-top: 1px solid var(--black);
        `}
      >
        <div
          className="type--large"
          css={css`
            grid-column: span 12;
            margin-bottom: 4rem;
          `}
        >
          Additional Information
        </div>
        <div
          css={css`
            grid-column: span 6;
            border-top: 1px solid var(--black);
            margin-bottom: 6rem;

            @media (max-width: 900px) {
              grid-column: span 12;
              margin-bottom: 0;
            }
          `}
        >
          {data.project.additionalInformation
            .map((information, i) => (
              <Accordion key={i} title={information.title}>
                <PortableText blocks={information._rawText} />
              </Accordion>
            ))
          }
        </div>
      </div>}
      {data.project.enableEoiForm && <div
        id="eoi-form"
        css={css`
          display: grid;
          grid-template-columns: repeat(12, 1fr);
          gap: var(--gutter);
          background-color: ${data.project.backgroundColor?.value || 'var(--beige)'};
          padding: 8rem var(--margin);
          border-top: 1px solid var(--black);
        `}
      >
        <div
          className="type--large"
          css={css`
            grid-column: span 6;

            @media (max-width: 900px) {
              grid-column: span 12;
              margin-bottom: 4rem;
            }
          `}
        >
          {data.project._rawEoiText && <PortableText blocks={data.project._rawEoiText} />}
        </div>
        <div
          css={css`
            grid-column-start: 9;
            grid-column-end: span 4;

            @media (max-width: 900px) {
              grid-column-start: 1;
              grid-column-end: span 12;
            }
          `}
        >
          <form
            id="eoi-form-submit"
            method="post"
            onSubmit={handleFormSubmit}
          >
            <input
              type="text"
              name="website"
              css={css`
                display: none;
              `}
              tabIndex="-1"
              autoComplete="off"
            />
            <input
              className="type--medium"
              css={css`
                width: 100%;
                font-family: 'Maax Medium', sans-serif;
                background-color: transparent;
                padding: 1.7rem 0 1.4rem;
                border: 0;
                border-bottom: 1px solid var(--black);

                @media (max-width: 800px) {
                  margin-bottom: 1rem;
                }

                &::placeholder {
                  color: rgba(0, 0, 0, 0.3);
                }

                &:focus::placeholder {
                  color: transparent;
                }
              `}
              type="text"
              name="firstName"
              placeholder="First Name*"
              required
            />
            <input
              className="type--medium"
              css={css`
                width: 100%;
                font-family: 'Maax Medium', sans-serif;
                background-color: transparent;
                padding: 1.7rem 0 1.4rem;
                border: 0;
                border-bottom: 1px solid var(--black);

                &::placeholder {
                  color: rgba(0, 0, 0, 0.3);
                }

                &:focus::placeholder {
                  color: transparent;
                }
              `}
              type="text"
              name="lastName"
              placeholder="Last Name*"
              required
            />
            <input
              className="type--medium"
              css={css`
                width: 100%;
                font-family: 'Maax Medium', sans-serif;
                background-color: transparent;
                padding: 1.7rem 0 1.4rem;
                border: 0;
                border-bottom: 1px solid var(--black);

                &::placeholder {
                  color: rgba(0, 0, 0, 0.3);
                }

                &:focus::placeholder {
                  color: transparent;
                }
              `}
              type="email"
              name="email"
              placeholder="Email Address*"
              required
            />
            <input
              className="type--medium"
              css={css`
                width: 100%;
                font-family: 'Maax Medium', sans-serif;
                background-color: transparent;
                padding: 1.7rem 0 1.4rem;
                border: 0;
                border-bottom: 1px solid var(--black);

                &::placeholder {
                  color: rgba(0, 0, 0, 0.3);
                }

                &:focus::placeholder {
                  color: transparent;
                }
              `}
              type="tel"
              name="telephone"
              placeholder="Telephone"
            />
            <input
              className="type--medium"
              css={css`
                width: 100%;
                font-family: 'Maax Medium', sans-serif;
                background-color: transparent;
                padding: 1.7rem 0 1.4rem;
                border: 0;
                border-bottom: 1px solid var(--black);
                margin-bottom: 1.2rem;

                &::placeholder {
                  color: rgba(0, 0, 0, 0.3);
                }

                &:focus::placeholder {
                  color: transparent;
                }
              `}
              type="text"
              name="postCode"
              placeholder="Post Code"
            />
            <div
              css={css`
                display: flex;
                align-items: flex-start;
                gap: 1rem;
                margin-bottom: 4rem;
              `}
            >
              <input
                type="checkbox"
                id="eoi-privacy-checkbox"
                required
                css={css`
                  margin-top: 0.3rem;
                  appearance: none;
                  width: 1.6rem;
                  height: 1.6rem;
                  border: 1px solid var(--black);
                  background-color: transparent;
                  cursor: pointer;
                  
                  &:checked {
                    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24'%3E%3Cpath d='M6 6l12 12M6 18L18 6' stroke='black' stroke-width='2' fill='none'/%3E%3C/svg%3E");
                    background-position: center;
                    background-repeat: no-repeat;
                    background-size: 1.2rem;
                  }
                `}
              />
              <label 
                htmlFor="eoi-privacy-checkbox" 
                className="font--medium type--extra-extra-small"
                css={css`
                  position: relative;
                  top: 0.2rem;
                  cursor: pointer;

                  a {
                    &:hover {
                      text-decoration: none;
                    }
                  }
                `}
              >
                I have read and agree to the <Link to="/privacy-policy/">Privacy Policy</Link>.
              </label>
            </div>
            <Button
              css={css`
                width: 100%;
                background-color: ${data.project.backgroundColor?.value || 'transparent'};
              `}
              submit="eoi-form-submit"
              disabled={formStatus === 'submitting' || formStatus === 'success'}
              text={formStatus === 'submitting' ? 'Submitting...' : formStatus === 'success' ? 'Thank You' : 'Sign Up'}
              arrow={<ArrowRight />}
            />
            {formStatus === 'error' && (
              <div
                className="type--small"
                css={css`
                  color: red;
                  margin-top: 1rem;
                `}
              >
                Sorry, there was an error submitting your form. Please try again.
              </div>
            )}
          </form>
          {(formStatus === 'success' && data.project._rawEoiCompletionText) && (
            <div
              className="type--body"
              css={css`
                margin-top: 2.4rem;
              `}
              onClick={handleEoiCompletionClick}
            >
              <PortableText blocks={data.project._rawEoiCompletionText} />
            </div>
          )}
        </div>
      </div>}
      {data.project.related.length > 0 && <div
        css={css`
          background-color: var(--white);
          padding: 8rem 0;
          border-top: 1px solid var(--black);
        `}
      >
        <div
          className="type--large"
          css={css`
            margin-bottom: 4rem;
            padding: 0 var(--margin);
          `}
        >
          Related Projects
        </div>
        <ProjectTable projects={data.project.related} />
      </div>}
      <div
        css={css`
          background-color: var(--white);
          padding: 2rem var(--margin) 8rem;
        `}
      >
        <Button
          external={false}
          url="/projects"
          text="Back to Projects"
          arrow={<ArrowLeft />}
        />
      </div>
    </div>
  )
}

export const query = graphql`
  query ProjectTemplateQuery($slug: String!) {
    project: sanityProject(slug: {current: {eq: $slug}}) {
      introForegroundColor
      introMediaType
      introImage {
        ...Image
      }
      introImageFormat
      introVideo
      title
      status {
        status
      }
      projectCode
      _rawOverview
      simpleAddress
      completion
      information {
        _key
        title
        _rawText
      }
      type {
        id
        title
        slug {
          current
        }
      }
      scale
      buttons {
        _key
        title
        url
      }
      content {
        ... on SanityImage {
          _key
          _type
          ...Image
        }
        ... on SanityHeading {
          _key
          _type
          text
        }
        ... on SanityGallery {
          _key
          _type
          images {
            image {
              asset {
                gatsbyImageData(width: 990, formats: WEBP)
              }
            }
            caption
          }
        }
        ... on SanityDoubleImage {
          _key
          _type
          imageOne {
            ...Image
          }
          imageTwo {
            ...Image
          }
        }
        ... on SanityImageQuote {
          _key
          _type
          imagePosition
          image {
            ...Image
          }
          _rawQuote
          quoteAuthor
        }
        ... on SanityImageText {
          _key
          _type
          imagePosition
          image {
            ...Image
          }
          _rawText
        }
        ... on SanityVideo {
          _key
          _type
          description
          title
          video
        }
      }
      additionalInformation {
        _key
        _rawText
        title
      }
      enableEoiForm
      backgroundColor {
        value
      }
      _rawEoiText
      _rawEoiCompletionText
      projectName
      projectId
      rotationName
      related {
        id
        slug {
          current
        }
        hoverType
        hoverImage {
          asset {
            gatsbyImageData(width: 1980, formats: WEBP)
          }
        }
        hoverFullBleedForegroundColor
        hoverFullBleedImage {
          asset {
            gatsbyImageData(width: 1980, formats: WEBP)
          }
        }
        hoverColor {
          value
        }
        title
        projectCode
        type {
          id
          title
          filter
          slug {
            current
          }
        }
        status {
          status
        }
        city
      }
    }
  }
`
